import { stripHtmlTags } from '@change-corgi/core/html';

import { getUsState } from 'src/app/shared/utils/unitedStatesMapping';

import type {
	AllDmResponses,
	BuildingBlockDms,
	DMs,
	NormalizedDecisionMaker,
	SortingMetadata,
} from '../../../../shared/types';
import {
	MAX_DISPLAYED_DM_CARDS_ON_PETITION,
	MAX_DISPLAYED_DMS_ON_LB_CARD,
	PRIMARY_ROLE_KEYWORDS,
} from '../../../../shared/types';
import type { NormalizedDMsAndLBs } from '../../types';

function sortMetadata(a: SortingMetadata, b: SortingMetadata): number {
	// First prioritize those with a response
	if (a.hasResponse !== b.hasResponse) return a.hasResponse ? -1 : 1;
	// Next prioritize highest constituentCounts
	if (a.constituentCount !== b.constituentCount) return b.constituentCount - a.constituentCount;
	// Next prioritize those auto-added by DMI
	if (a.isAutoAdded !== b.isAutoAdded) return a.isAutoAdded ? -1 : 1;
	return 0;
}

export function sortAllDms(dms: NormalizedDecisionMaker[]): NormalizedDecisionMaker[] {
	const preppedDms = dms.map((dm) => ({
		dm,
		sortingMetadata: {
			hasResponse: !!dm.response,
			constituentCount: dm.constituentCount,
			isAutoAdded: dm.isAutoAdded,
		},
	}));

	preppedDms.sort((a, b) => {
		return sortMetadata(a.sortingMetadata, b.sortingMetadata);
	});

	return preppedDms.map((x) => x.dm);
}

export const getLocation = (state: string | null, title: string | null): string | null => {
	if (!state) return null;

	const usKeywords = ['U.S.', 'US', 'United States', 'State'];
	const hasUsKeyword = usKeywords.some((keyword) => title?.includes(keyword));

	if (hasUsKeyword) return null;

	const fullStateName = getUsState(state);
	if (title?.includes(fullStateName)) return null;

	return fullStateName;
};

export function normalizeDms(dms: DMs, allDmResponses: AllDmResponses): NormalizedDecisionMaker[] {
	// eslint-disable-next-line complexity
	return dms.map((dm) => {
		const dmResponse = allDmResponses.find((response) => response.decisionMaker.id === dm.id);

		const constituentCountLocalized = dm.targetPetitionAssociationData?.constituentCountAmongSignersLocalized ?? '0';
		const constituentCount = parseInt(constituentCountLocalized.replace(/,/g, ''), 10) || 0;

		return {
			id: dm.id,
			displayName: dm.displayName,
			slug: dm.slug,
			title: dm.title ? dm.title.replace(/\bUS\b/g, 'U.S.') : null,
			titleStrippedDown: dm.title ? getTitleStrippedDown(dm.title) : null,
			isAutoAdded: dm.targetPetitionAssociationData?.isAutoAdded || false,
			location: getLocation(dm.state, dm.title),
			photoUrl: dm.photo?.userMedium.processing ? null : (dm.photo?.userMedium.url ?? null),
			constituentCount,
			constituentCountLocalized,
			isFormer: !!dm.title?.startsWith('Former'),
			publiclyVisible: dm.publiclyVisible,
			type: dm.type,
			...(dmResponse?.message ? { response: { id: dmResponse.id, message: stripHtmlTags(dmResponse.message) } } : {}),
		};
	});
}

export function getPrimaryDms(politicians: NormalizedDecisionMaker[]): NormalizedDecisionMaker[] {
	const primaries = politicians.filter((dm) => {
		if (!dm.title) return false;
		const title = dm.title.toLowerCase();
		return PRIMARY_ROLE_KEYWORDS.some((role) => title.includes(role));
	});
	return primaries;
}

export function getTitleStrippedDown(title: string): string {
	const titleBeforeHyphen = title.split(' - ')[0].trim();
	return titleBeforeHyphen.replace(/\bUS\b/g, 'U.S.');
}

export function getLegislativeBodies(
	dms: NormalizedDecisionMaker[],
): Array<{ groupTitle: string; groupDms: NormalizedDecisionMaker[] }> {
	const groups = new Map<string, NormalizedDecisionMaker[]>();
	for (const dm of dms) {
		if (dm.title) {
			const key = getTitleStrippedDown(dm.title);

			// 'Senator' is not a legislative body. Replace with 'Senate'.
			//  Strip 'Former' prefix in attempt to match any existing legislative bodies.
			const groupKey = key.includes('Senator') ? key.replace('Former ', '').replace('Senator', 'Senate') : key;

			if (!groups.has(groupKey)) {
				groups.set(groupKey, []);
			}
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			groups.get(groupKey)!.push(dm);
		}
	}

	return Array.from(groups.entries())
		.filter(([, items]) => items.length > 1)
		.map(([key, items]) => ({ groupTitle: key, groupDms: items }));
}

export function formatAllBuckets(
	primaryDms: NormalizedDecisionMaker[],
	legislativeBodies: Array<{
		title: string;
		location: string | null;
		responseCount: number;
		decisionMakers: NormalizedDecisionMaker[];
	}>,
	individualPoliticians: NormalizedDecisionMaker[],
	remainingDMs: NormalizedDecisionMaker[],
): BuildingBlockDms {
	const primaryDmsFormatted = primaryDms.map((dm) => ({ component: 'primary' as const, componentDm: dm }));
	const legislativeBodiesFormatted = legislativeBodies.map(({ decisionMakers, ...rest }) => {
		const displayedDMs = decisionMakers.slice(0, MAX_DISPLAYED_DMS_ON_LB_CARD);

		const viewMoreCountRaw = decisionMakers.length - MAX_DISPLAYED_DMS_ON_LB_CARD;
		const viewMoreCount = viewMoreCountRaw > 0 ? viewMoreCountRaw : 0;

		return {
			component: 'legislativeBody' as const,
			componentDms: [...decisionMakers],
			componentAttributes: {
				...rest,
				displayedDMs,
				viewMoreCount,
			},
		};
	});
	const individualPoliticiansFormatted = individualPoliticians.map((dm) => ({
		component: 'individual' as const,
		componentDm: dm,
	}));
	const remainingDMsFormatted = remainingDMs.map((dm) => ({
		component: 'individual' as const,
		componentDm: dm,
	}));

	return [
		...primaryDmsFormatted,
		...legislativeBodiesFormatted,
		...individualPoliticiansFormatted,
		...remainingDMsFormatted,
	];
}

export function sortAllBuildingBlocks(allFormattedBucketsOfDms: BuildingBlockDms): BuildingBlockDms {
	const grabTopDmFromEachBlock = (block: BuildingBlockDms[number]) => {
		if (block.component === 'primary' || block.component === 'individual') {
			return block.componentDm;
		}
		return block.componentDms[0];
	};

	const preppedBlocks = allFormattedBucketsOfDms.map((block) => {
		const dm = grabTopDmFromEachBlock(block);
		return {
			block,
			sortingMetadata: {
				hasResponse: !!dm.response,
				constituentCount: dm.constituentCount,
				isAutoAdded: !!dm.isAutoAdded,
			},
		};
	});

	preppedBlocks.sort((a, b) => {
		return sortMetadata(a.sortingMetadata, b.sortingMetadata);
	});

	// Make an exception to prioritization rule
	// Return one primary DM within the allowed display range if primary DMs exist
	// Return one legislative body within the allowed display range if lbs exist
	if (preppedBlocks.length > MAX_DISPLAYED_DM_CARDS_ON_PETITION) {
		if (
			preppedBlocks.some((x) => x.block.component === 'legislativeBody') &&
			!preppedBlocks.slice(0, MAX_DISPLAYED_DM_CARDS_ON_PETITION).some((x) => x.block.component === 'legislativeBody')
		) {
			const firstLBDmIndex = preppedBlocks.findIndex((x) => x.block.component === 'legislativeBody');
			const [firstLB] = preppedBlocks.splice(firstLBDmIndex, 1);
			preppedBlocks.splice(0, 0, firstLB);
		}
		if (
			preppedBlocks.some((x) => x.block.component === 'primary') &&
			!preppedBlocks.slice(0, MAX_DISPLAYED_DM_CARDS_ON_PETITION).some((x) => x.block.component === 'primary')
		) {
			const firstPrimaryDmIndex = preppedBlocks.findIndex((x) => x.block.component === 'primary');
			const [firstPrimary] = preppedBlocks.splice(firstPrimaryDmIndex, 1);
			preppedBlocks.splice(0, 0, firstPrimary);
		}
	}

	return preppedBlocks.map((x) => x.block);
}

export function formatForDMsAndLBs(dmBuckets: BuildingBlockDms): NormalizedDMsAndLBs {
	return dmBuckets.map((item) => {
		if (item.component === 'primary' || item.component === 'individual') {
			return {
				type: 'single',
				displayedDMs: [item.componentDm],
			};
		}
		return {
			type: 'legislative_body',
			title: item.componentAttributes.title,
			bodyTitle: item.componentAttributes.title,
			location: item.componentAttributes.location,
			allDMs: [...item.componentDms],
			displayedDMs: [...item.componentAttributes.displayedDMs],
			responseCount: item.componentAttributes.responseCount,
			viewMoreCount: item.componentAttributes.viewMoreCount,
		};
	});
}
